import { useQuery } from '@tanstack/react-query';
import { leadApi } from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import { useState } from 'react';

const statusStyle: Record<string, string> = {
  new: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  contacted: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  converted: 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary',
  closed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export function DashboardTransactions() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['transactions', page],
    queryFn: () => leadApi.list(page, 10),
  });

  const leads = data?.leads || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Info Banner */}
      <Card className="rounded-2xl border-0 bg-primary text-primary-foreground overflow-hidden">
        <CardContent className="p-5 flex items-center gap-6">
          <FileText className="h-10 w-10 shrink-0 opacity-80" />
          <div className="flex-1">
            <h3 className="font-bold text-lg">All Transactions</h3>
            <p className="text-sm opacity-80 mt-0.5">Manage and track all property inquiries and leads</p>
          </div>
          <div className="hidden md:flex gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold">{total}</div>
              <div className="text-[10px] opacity-70">Total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{leads.filter((l: any) => l.status === 'converted').length}</div>
              <div className="text-[10px] opacity-70">Converted</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="rounded-2xl border-0 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="p-3 w-10"><Checkbox /></th>
                <th className="p-3 text-left font-medium text-muted-foreground">Order ID</th>
                <th className="p-3 text-left font-medium text-muted-foreground">Date</th>
                <th className="p-3 text-left font-medium text-muted-foreground">Customer</th>
                <th className="p-3 text-left font-medium text-muted-foreground">Phone</th>
                <th className="p-3 text-left font-medium text-muted-foreground">Source</th>
                <th className="p-3 text-left font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No transactions yet</td></tr>
              ) : leads.map((lead: any) => (
                <tr key={lead.id} className="border-b hover:bg-muted/20 transition-colors">
                  <td className="p-3"><Checkbox /></td>
                  <td className="p-3 font-mono text-xs">#{lead.id?.slice(0, 8)}</td>
                  <td className="p-3 text-muted-foreground">{new Date(lead.created_at).toLocaleDateString()}</td>
                  <td className="p-3 font-medium">{lead.name}</td>
                  <td className="p-3 text-muted-foreground">{lead.phone}</td>
                  <td className="p-3 text-muted-foreground">{lead.source || 'direct'}</td>
                  <td className="p-3">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-semibold ${statusStyle[lead.status] || statusStyle.new}`}>
                      {lead.status || 'new'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between p-4 border-t">
          <span className="text-xs text-muted-foreground">Page {page} of {totalPages} ({total} total)</span>
          <div className="flex gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
              <Button key={i} variant={page === i + 1 ? 'default' : 'outline'} size="icon" className="h-8 w-8 text-xs" onClick={() => setPage(i + 1)}>
                {i + 1}
              </Button>
            ))}
            <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
